import type { DataKeys, Meta } from 'zhead'

import AuthScope from '~/enums/auth-scope'
import type ImageType from '~/enums/image-type'
import Page from '~/enums/page'
import type { BrandData } from '~/types/brands'
import type { CartProductData } from '~/types/cart'
import type { CategoryData } from '~/types/categories'
import type { ParamCondition } from '~/types/json-api'
import type { ProductData, ProductLabel, ProductShortData } from '~/types/products'
import type { SpecialistData } from '~/types/specialists'
import type { UtmParameters } from '~/types/utm-parameters'

export default {
  // Paths.
  getArticlePath: (articleData: any): string => `${Page.Articles}${articleData.attributes.slug}-${articleData.id}/`,
  getBrandPath: (brandData: BrandData): string => `${Page.Brands}${brandData.attributes.slug}-${brandData.id}/`,
  getCategoryPath: (categoryData: CategoryData): string =>
    `${Page.Categories}${categoryData.attributes.slug}-${categoryData.id}/`,
  getCollectionPath: (collectionData: any): string =>
    `${Page.Collections}${collectionData.attributes.slug}-${collectionData.id}/`,
  getListPath: (listData: any): string => `${Page.Lists}${listData.id}/`,
  getProductPath: (productData: ProductShortData): string =>
    `${Page.Products}${productData.attributes.slug}-${productData.id}/`,
  getSpecialistPath: (specialistData: SpecialistData): string =>
    `${Page.Specialists}${specialistData.attributes.slug}-${specialistData.id}/`,
  getSpecialOfferPath: (specialOfferData: any): string =>
    `${Page.SpecialOffers}${specialOfferData.attributes.slug}-${specialOfferData.id}/`,

  calculateDistance: (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const r = 6371000
    const toRad = (angle: number): number => (angle * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(toRad(lat1))
      * Math.cos(toRad(lat2))
      * Math.sin(dLon / 2)
      * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return r * c
  },
  convertKopecksToRubles: (kopecks: number | null): number | null => kopecks === null ? null : kopecks / 100,
  convertRublesToKopecks: (rubles: string | number | null): number | null => {
    if (rubles === null) {
      return null
    }

    if (typeof rubles === 'string') {
      if (rubles.trim() === '') {
        return null
      }

      rubles = Number(rubles.replace(',', '.'))
    }

    return Math.round(100 * rubles)
  },
  debounce<F extends (...args: unknown[]) => void | Promise<void>>(
    fn: F,
    delayInMs: number
  ): (...args: Parameters<F>) => void {
    let timeout: number | null = null

    return (...args: Parameters<F>): void => {
      if (timeout !== null) {
        clearTimeout(timeout)
      }

      timeout = window.setTimeout((): void => {
        fn(...args)
        timeout = null
      }, delayInMs)
    }
  },
  getDeclension: (number: number, cases: string[]): string =>
    cases[number % 100 > 4 && number % 100 < 20 ? 2 : [2, 0, 1, 1, 1, 2, 2, 2, 2, 2][number % 10]],
  getFullName (data: any): string | null {
    const { firstName, lastName, patronymic } = data.attributes

    if (firstName && lastName && patronymic) {
      return `${lastName} ${firstName} ${patronymic}`
    }

    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }

    return firstName
  },
  getIdFromSlugAndId: (slugAndId: string): string | null => slugAndId.split('-').pop() ?? null,
  getImageUrl (imagePaths: any, imageTypes: ImageType[]): string | null {
    for (const x of imageTypes) {
      if (imagePaths[x]) {
        return imagePaths[x]
      }
    }

    const keys = Object.keys(imagePaths)

    return keys.length ? keys[0] : null
  },
  getImageUrls (imagesData: any[]): object | null {
    if (!imagesData.length) {
      return null
    }

    const imagesUrls: any = {}

    for (const x of imagesData) {
      imagesUrls[x.meta.type] = x.meta.url
    }

    return imagesUrls
  },
  getPageTitle: (title: string, authScope: AuthScope): string =>
    `${title} — ${AuthScope.getUserSectionName(authScope)} — iTAB`,
  getParams: (paramConditions: ParamCondition[]): object => Object
    .fromEntries(
      paramConditions
        .filter((x: ParamCondition): any => x.condition)
        .map((x: ParamCondition): any[] => [x.name, x.value])
    ),
  getPreviewMetadata: (previewUrl: string): (Meta & DataKeys)[] => [
    { name: 'og:image', property: 'og:image', content: previewUrl },
    { name: 'vk:image', property: 'vk:image', content: previewUrl },
    { name: 'twitter:image', content: previewUrl }
  ],
  getProductDiscountedPrice (productData: ProductData | CartProductData): number | null {
    const { discountInKopecks, quantity } = productData.meta
    const productPrice = this.getProductPrice(productData)

    if (discountInKopecks) {
      return this.convertKopecksToRubles(
        Math.round((this.convertRublesToKopecks(productPrice) ?? 0) - discountInKopecks / quantity!)
      )
    }

    return productPrice
  },
  getProductImageUrl (productData: ProductData | CartProductData, imageTypes: ImageType[]): string | null {
    const imageUrls = this.getImageUrls(productData.relationships.images.data)

    return imageUrls ? this.getImageUrl(imageUrls, imageTypes) : null
  },
  getProductPrice: (productData: ProductData | CartProductData): number | null => productData.attributes.price,
  getReviewAuthorName: (authorName: string | null, isAnonymous: boolean): string =>
    isAnonymous ? 'Анонимный покупатель' : (authorName ?? 'Имя не указано'),
  getTotalPages: (totalItems: number, pageSize: number): number | null => totalItems === 0
    ? null
    : Math.ceil(totalItems / pageSize),
  getUtmParameters (): UtmParameters | null {
    const localStorageKey = 'utmParameters'
    const utmParametersAsJsonString = localStorage.getItem(localStorageKey)

    if (utmParametersAsJsonString === null) {
      return null
    }

    let utmParametersFromLocalStorage: UtmParameters | null = null

    try {
      utmParametersFromLocalStorage = JSON.parse(utmParametersAsJsonString)
    } catch (e) {
      console.error(e)

      localStorage.removeItem(localStorageKey)
    }

    if (typeof utmParametersFromLocalStorage !== 'object' || utmParametersFromLocalStorage === null) {
      localStorage.removeItem(localStorageKey)

      return null
    }

    const { utmCampaign, utmContent, utmMedium, utmSource, utmTerm } = utmParametersFromLocalStorage
    const utmParameters = this.purifyUtmParameters({ utmCampaign, utmContent, utmMedium, utmSource, utmTerm })

    if (utmParameters === null) {
      localStorage.removeItem(localStorageKey)

      return null
    }

    return utmParameters
  },
  isProductImageBlackAndWhite (productData: ProductData): boolean {
    const isOutOfStock = productData.meta?.isOutOfStock

    if (
      isOutOfStock
      && productData.meta?.labels?.some(
        (x: ProductLabel): boolean => ['Скоро в продаже', 'Предзаказ'].includes(x?.title)
      )
    ) {
      return false
    }

    return isOutOfStock ?? false
  },
  parseLabels (params: Record<string, string | number | boolean> | undefined): ProductLabel[] {
    if (!params) {
      return []
    }

    const labels: ProductLabel[] = []

    for (let i = 1; i <= 5; ++i) {
      const fieldName = `label${i}`
      const labelJson = params[fieldName]

      if (typeof labelJson === 'string') {
        try {
          const parsedLabel = JSON.parse(labelJson)

          if (
            typeof parsedLabel.backgroundColor === 'string'
            && typeof parsedLabel.color === 'string'
            && typeof parsedLabel.title === 'string'
          ) {
            labels.push(parsedLabel)
          }
        } catch (e) {
          console.error('Failed to parse a label.', e)
        }
      }
    }

    return labels
  },
  purifyUtmParameters (utmParameters: UtmParameters): object | null {
    Object.keys(utmParameters).forEach((x: string): void => {
      if (typeof utmParameters[x] !== 'string' || utmParameters[x] === '') {
        delete utmParameters[x]
      }
    })

    const requiredUtmParameters = ['utmSource']

    for (const x of requiredUtmParameters) {
      if (!Object.prototype.hasOwnProperty.call(utmParameters, x)) {
        return null
      }
    }

    return utmParameters
  },
  roundToTwo (value: number): number {
    return +(Math.round(Number(`${value}e+2`)) + 'e-2')
  },
  validateField (field: any): string | null {
    const { isRequired, labelText, maxLength, modelValue, placeholder, validate } = field.props

    if (!isRequired) {
      return null
    }

    const name = labelText || placeholder

    if (!modelValue || field.maskPrefix === modelValue) {
      field.isValid = false

      if (field.isChanged === false) {
        field.isChanged = true
      }

      return `Поле «${name}» не может быть пустым.`
    }

    if (typeof validate === 'function' && typeof maxLength === 'number' && !field.isValid) {
      // let maxLength
      //
      // for (const x of field.$vnode.elm.children) {
      //   if (x.localName.includes('input')) {
      //     maxLength = field._name.includes('PhoneNumber')
      //       ? field.numberCount()
      //       : x.dataset.mask.length
      //   }
      // }
      //
      // if (maxLength === undefined) {
      //   console.error(`Please set mask for ${name}`)
      // }

      field.isValid = false

      if (field.isChanged === false) {
        field.isChanged = true
      }

      return `В поле «${name}» ${this.getDeclension(maxLength, ['должна', 'должно', 'должно'])} быть `
        + `${maxLength} ${this.getDeclension(maxLength, ['цифра', 'цифры', 'цифр'])}.`
    }

    return null
  }
}
